/**
 * 2026-06-20 — UI helpers for "value + GST" pricing display.
 *
 * Shared by PlanCard (subscriptions) and Topup pack cards (wallet).
 */

const GST_RATE_BPS = 1800;   // 18%


/** Indian-format rupees, no decimals (₹2,999). */
export function inr(amount) {
  const n = Math.round(amount || 0);
  return `₹${n.toLocaleString("en-IN")}`;
}

/** Indian-format rupees with 2 decimals (₹3,538.82) — used when gross is non-round. */
export function inr2(amount) {
  return `₹${(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

// 2026-06-29 — multi-currency support (Sprint 4 / Airwallex).
// Currencies the SPA renders today. Add to both maps when seeding a new
// foreign-currency plan in the admin.
const CURRENCY_SYMBOL = {
  INR: "₹", USD: "$", GBP: "£", EUR: "€", AUD: "A$", NZD: "NZ$",
  CAD: "C$", AED: "AED ", SGD: "S$", ZAR: "R",
};
const CURRENCY_LOCALE = {
  INR: "en-IN", USD: "en-US", GBP: "en-GB", EUR: "en-IE", AUD: "en-AU",
  NZD: "en-NZ", CAD: "en-CA", AED: "en-AE", SGD: "en-SG", ZAR: "en-ZA",
};

/**
 * Currency-aware price formatter. INR keeps the legacy ₹X (no decimals)
 * shape; every other currency renders with 2 decimals. Falls back to a
 * plain "<currency> <amount>" string if the currency is unknown so the
 * UI never shows an empty price.
 *
 * @param {string} currency  — ISO 4217 code (INR, USD, GBP, ...)
 * @param {number} amount    — amount in MAJOR units (rupees, dollars, ...)
 */
export function formatMoney(currency, amount) {
  const cur = (currency || "INR").toUpperCase();
  const n = amount ?? 0;
  if (cur === "INR") return inr(n);
  const symbol = CURRENCY_SYMBOL[cur];
  const locale = CURRENCY_LOCALE[cur] || "en-US";
  const body = n.toLocaleString(locale, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return symbol ? `${symbol}${body}` : `${cur} ${body}`;
}

/**
 * Resolve a plan's display price + currency from the Sprint 4 fields.
 * Returns { currency, majorAmount } so the caller renders consistently
 * for both INR (`price_inr` in rupees) and foreign plans (`price_minor`
 * in cents / pence / etc).
 */
export function planDisplayPrice(plan) {
  const currency = (plan?.currency || "INR").toUpperCase();
  if (currency === "INR") {
    return { currency, majorAmount: plan?.price_inr ?? null };
  }
  const minor = plan?.price_minor;
  // Every launch currency (USD/GBP/AUD/EUR) is 2-decimal — divide by 100.
  // Add 0-decimal currencies (JPY/KRW) here when they're ever seeded.
  return {
    currency,
    majorAmount: minor != null ? minor / 100 : null,
  };
}


/**
 * Resolve display lines for a plan / pack that may be tax-inclusive or
 * tax-exclusive.
 *
 * @param {object} item — { price_inr, is_tax_inclusive } OR { price_inr_paise, ... }
 * @returns {{ baseLabel, taxLine, totalLabel, isExclusive }}
 *   baseLabel  — headline price (₹X). For inclusive plans this IS the total.
 *   taxLine    — secondary "+ 18% GST" line (only on exclusive items).
 *   totalLabel — explicit total when exclusive ("Total ₹Y"). undefined when inclusive.
 *   isExclusive — convenience boolean for callers that need to branch UI.
 */
export function priceBreakdown(item) {
  const isExclusive = item && item.is_tax_inclusive === false;
  // Read rupees from whichever field exists.
  const baseRupees = item?.price_inr ?? (item?.price_inr_paise != null
    ? item.price_inr_paise / 100
    : 0);
  const gst = Math.round(baseRupees * GST_RATE_BPS / 10000);
  const total = baseRupees + gst;

  if (!isExclusive) {
    return {
      baseLabel: inr(baseRupees),
      taxLine: "incl. 18% GST",
      isExclusive: false,
    };
  }
  return {
    baseLabel: inr(baseRupees),
    taxLine: `+ 18% GST (${inr(gst)})`,
    totalLabel: `Total ${inr(total)}`,
    isExclusive: true,
  };
}
